// src/pages/InboxPage.js
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowLeft, Phone, Video, Search, MoreHorizontal } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { fetchOrgSessions } from "../utils/orgData";
import {
  buildCoachThreadSessionList,
  COACH_THREAD_SYNC_EVENT,
  getCoachThreadIncomingAt,
  listenCoachThread,
  markCoachThreadRead,
  sendCoachThreadMessage,
} from "../utils/coachThread";
import { getCoachThreadSenderLabel, getSessionDisplayName } from "../utils/sessionPrivacy";

export default function InboxPage() {
  const { user, activeOrgId } = useAuth();
  const [threads, setThreads] = useState([]);
  const [queryText, setQueryText] = useState("");
  const [activeThread, setActiveThread] = useState(null);
  const [threadRows, setThreadRows] = useState([]);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [isMobile, setIsMobile] = useState(() => (typeof window !== "undefined" ? window.innerWidth <= 720 : false));
  const [mobileView, setMobileView] = useState("list");

  useEffect(() => {
    let mounted = true;
    if (!user?.uid) return undefined;
    const orgId = activeOrgId || user.orgId || "org_default";
    const refresh = () => {
      fetchOrgSessions(user, orgId)
        .then((sessions) => {
          if (!mounted) return;
          const sorted = buildCoachThreadSessionList(sessions, user, user?.role || "member");
          setThreads(sorted);
          setActiveThread((prev) => {
            if (!sorted.length) return null;
            if (!prev) return sorted[0];
            const matched = sorted.find((row) => String(row._threadId || row.id) === String(prev._threadId || prev.id));
            return matched || sorted[0];
          });
          if (isMobile && !sorted.length) {
            setMobileView("list");
          }
        })
        .catch(() => {});
    };
    refresh();
    const onSync = () => refresh();
    window.addEventListener(COACH_THREAD_SYNC_EVENT, onSync);
    const id = setInterval(refresh, 30000);
    return () => {
      mounted = false;
      window.removeEventListener(COACH_THREAD_SYNC_EVENT, onSync);
      clearInterval(id);
    };
  }, [user, activeOrgId, isMobile]);

  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth <= 720;
      setIsMobile(mobile);
      if (!mobile) setMobileView("list");
    };
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!activeThread || !user?.uid) {
      setThreadRows([]);
      return;
    }
    const orgId = activeOrgId || user.orgId || "org_default";
    const sessionId = activeThread.id || activeThread.createdAt;
    if (!sessionId) return;
    const unsub = listenCoachThread(user, orgId, sessionId, (rows) => {
      setThreadRows(rows || []);
    });
    return () => {
      if (typeof unsub === "function") unsub();
    };
  }, [activeThread, user, activeOrgId]);

  useEffect(() => {
    if (!activeThread || !user?.uid) return;
    const sessionId = activeThread._threadId || activeThread.id || activeThread.createdAt;
    const incomingAt = getCoachThreadIncomingAt(activeThread, user?.role || "member");
    if (!sessionId || !incomingAt) return;
    markCoachThreadRead(user.uid, sessionId, incomingAt);
  }, [activeThread, user]);

  const getTitle = useCallback((session) => {
    if (!session) return "Member";
    if (user?.role === "member") return "Coach";
    return getSessionDisplayName(session, user?.role || "member");
  }, [user?.role]);

  const getPreview = useCallback((session) => {
    if (!session) return "";
    const coachAt = Number(session.lastCoachMessageAt || 0);
    const memberAt = Number(session.lastMemberMessageAt || 0);
    if (user?.role === "member") {
      if (coachAt > memberAt) return "Coach replied";
      if (memberAt > coachAt) return "You replied";
      return "Start the conversation";
    }
    if (memberAt > coachAt) return "Member replied";
    if (coachAt > memberAt) return "You replied";
    return "Start the conversation";
  }, [user?.role]);

  const getInitials = useCallback((name) => {
    if (!name) return "U";
    const parts = String(name).trim().split(/\s+/);
    const first = parts[0]?.[0] || "";
    const last = parts.length > 1 ? parts[parts.length - 1]?.[0] : "";
    return (first + last).toUpperCase();
  }, []);

  const filteredThreads = useMemo(() => {
    if (!queryText.trim()) return threads;
    const q = queryText.toLowerCase();
    return threads.filter((t) => getTitle(t).toLowerCase().includes(q));
  }, [threads, queryText, getTitle]);

  return (
    <div className={`inbox-page${isMobile ? " mobile" : ""}${isMobile && mobileView === "thread" ? " thread-open" : ""}`}>
      <div className="inbox-sidebar">
        <div className="inbox-header">
          <h2>Inbox</h2>
          <div className="inbox-search">
            <Search size={16} />
            <input
              type="search"
              placeholder="Search messages"
              value={queryText}
              onChange={(e) => setQueryText(e.target.value)}
            />
          </div>
        </div>
        <div className="inbox-list">
          {filteredThreads.length === 0 ? (
            <div className="inbox-empty">No direct messages yet.</div>
          ) : (
            filteredThreads.map((thread) => (
              <button
                key={thread._threadId || thread.id || thread.createdAt}
                type="button"
                className={`inbox-item${String(activeThread?._threadId || activeThread?.id || "") === String(thread._threadId || thread.id || "") ? " active" : ""}`}
                onClick={() => {
                  setActiveThread(thread);
                  if (isMobile) setMobileView("thread");
                }}
              >
                <div className="inbox-avatar">{getInitials(getTitle(thread))}</div>
                <div className="inbox-item-body">
                  <div className="inbox-item-title">{getTitle(thread)}</div>
                  <div className="inbox-item-meta">
                    {getPreview(thread)} • {new Date(thread._lastTs || Date.now()).toLocaleDateString()}
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      <div className="inbox-thread">
        {activeThread ? (
          <>
            <div className="inbox-thread-header">
              {isMobile && (
                <button type="button" className="icon-btn inbox-back" onClick={() => setMobileView("list")} aria-label="Back">
                  <ArrowLeft size={18} />
                </button>
              )}
              <div className="inbox-thread-avatar">{getInitials(getTitle(activeThread))}</div>
              <div className="inbox-thread-head">
                <div className="inbox-thread-title">{getTitle(activeThread)}</div>
                <div className="inbox-thread-meta">
                  Last update {new Date(activeThread._lastTs || Date.now()).toLocaleString()}
                </div>
              </div>
              <div className="inbox-thread-actions">
                <button type="button" className="icon-btn" aria-label="Call"><Phone size={18} /></button>
                <button type="button" className="icon-btn" aria-label="Video"><Video size={18} /></button>
                <button type="button" className="icon-btn" aria-label="More"><MoreHorizontal size={18} /></button>
              </div>
            </div>
            <div className="inbox-thread-body">
              {threadRows.length ? (
                threadRows.map((msg, idx) => (
                  <div
                    key={`${msg.createdAt || idx}-${idx}`}
                    className={`inbox-msg${msg.senderId === user?.uid ? " mine" : ""}`}
                  >
                    <div className="inbox-msg-sender">{getCoachThreadSenderLabel(msg, activeThread, user?.role || "member")}</div>
                    <div className="inbox-msg-text">{msg.text || ""}</div>
                  </div>
                ))
              ) : (
                <div className="inbox-empty">No messages in this thread yet.</div>
              )}
            </div>
            <div className="inbox-thread-reply">
              <textarea
                rows={3}
                placeholder="Write a reply…"
                value={reply}
                onChange={(e) => setReply(e.target.value)}
              />
              <button
                type="button"
                className="btn btn-primary"
                disabled={!reply.trim() || sending}
                onClick={async () => {
                  const orgId = activeOrgId || user?.orgId || "org_default";
                  const sessionId = activeThread.id || activeThread.createdAt;
                  if (!sessionId || !reply.trim()) return;
                  setSending(true);
                  await sendCoachThreadMessage(user, orgId, sessionId, reply.trim());
                  setReply("");
                  setSending(false);
                }}
              >
                {sending ? "Sending…" : "Send"}
              </button>
            </div>
          </>
        ) : (
          <div className="inbox-empty">Select a conversation to view messages.</div>
        )}
      </div>
    </div>
  );
}
